import type { MapMode } from './types.js';

export type Tick = { position: number; major: boolean };

export function ControlPanel({
  mode,
  onModeChange,
  timeLabel,
  index,
  max,
  onIndexChange,
  onSettings,
  ticks,
  fetchedLabel,
}: {
  mode: MapMode;
  onModeChange: (m: MapMode) => void;
  timeLabel: string;
  index: number;
  max: number;
  onIndexChange: (i: number) => void;
  onSettings: () => void;
  ticks: Tick[];
  fetchedLabel: string;
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 pb-safe">
      <div className="pointer-events-auto mx-auto max-w-2xl bg-white/95 px-3 pt-2 pb-2 shadow-[0_-6px_24px_rgba(15,23,42,0.18)] backdrop-blur sm:m-3 sm:rounded-2xl">
        <ModeToggle mode={mode} onChange={onModeChange} />
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[10px] leading-tight text-slate-500">{fetchedLabel}</span>
          <button
            type="button"
            onClick={onSettings}
            className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 active:bg-slate-200"
            aria-label="設定・出典"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <circle cx="12" cy="8" r="1" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="mt-1.5 flex items-center justify-center gap-2">
          <span className="text-sm font-medium text-slate-700">{timeLabel}</span>
          {mode === 'forecast' && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
              1〜2日先は約130地点／3日目以降は約50地点（気象庁仕様）
            </span>
          )}
        </div>
        <div className="heat-slider relative mt-1.5">
          <div className="heat-slider__ticks pointer-events-none absolute top-0 h-3">
            {ticks.map((t, i) => (
              <span
                key={i}
                className="absolute top-0 -translate-x-1/2 bg-slate-900/40"
                style={{
                  left: `${t.position * 100}%`,
                  width: 1,
                  height: t.major ? 12 : 6,
                }}
              />
            ))}
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(max, 0)}
            value={Math.min(index, Math.max(max, 0))}
            onChange={(e) => onIndexChange(Number(e.target.value))}
            className="heat-slider__input relative h-2 w-full cursor-pointer rounded-full"
            aria-label="日時スライダー"
          />
        </div>
      </div>
    </div>
  );
}

export function ModeToggle({ mode, onChange }: { mode: MapMode; onChange: (m: MapMode) => void }) {
  return (
    <div className="flex w-full rounded-full bg-slate-100 p-0.5 text-sm">
      <ToggleButton active={mode === 'wbgt'} onClick={() => onChange('wbgt')} label="WBGT" />
      <ToggleButton active={mode === 'forecast'} onClick={() => onChange('forecast')} label="日別最高気温" />
      <ToggleButton active={mode === 'historical'} onClick={() => onChange('historical')} label="過去データ" />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-1 whitespace-nowrap rounded-full px-2 py-1 text-[13px] font-medium transition-colors',
        active ? 'bg-white text-slate-900 shadow' : 'text-slate-500',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
